import AddIcon from '@offline-mui/icons-material/Add'
import DeleteOutlinedIcon from '@offline-mui/icons-material/DeleteOutlined'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useEffect, useMemo, useState } from 'react'
import { accountingRequest, jsonInit } from '../api'
import { LeoHeader } from '../LeoHeader'

type ReferenceKind = 'forms-of-payment' | 'suppliers' | 'transaction-types'

type Column = { key: string; label: string; width?: number; options?: string[] }

export const REFERENCE_KINDS: Record<ReferenceKind, { title: string; singular: string; columns: Column[] }> = {
  'forms-of-payment': {
    title: 'Forms of payment',
    singular: 'form of payment',
    columns: [
      { key: 'code', label: 'Code', width: 90 },
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category', width: 160, options: ['Deposits', 'Credit cards', 'Transfers', 'Adjustments', 'Other'] },
      { key: 'description_en', label: 'Description (EN)' },
      { key: 'description_fr', label: 'Description (FR)' },
    ],
  },
  suppliers: {
    title: 'Suppliers',
    singular: 'supplier',
    columns: [
      { key: 'code', label: 'Code', width: 90 },
      { key: 'name', label: 'Supplier name' },
      { key: 'name_en', label: 'Name EN' },
      { key: 'name_fr', label: 'Name FR' },
      { key: 'description', label: 'Description' },
      { key: 'note', label: 'Note', width: 220 },
    ],
  },
  'transaction-types': {
    title: 'Transaction types',
    singular: 'transaction type',
    columns: [
      { key: 'code', label: 'Code', width: 90 },
      { key: 'description', label: 'Description' },
    ],
  },
}

function cellValue(row: any, key: string) {
  const value = row?.[key]
  if (value != null && String(value).trim() !== '') return String(value)
  // Name EN/FR fall back to supplier name when a locale-specific value is absent.
  if (key === 'name_en' || key === 'name_fr') return String(row?.name || '')
  return ''
}

function EditDialog({ kind, row, onClose, onSaved, onDeleted }: any) {
  const spec = REFERENCE_KINDS[kind as ReferenceKind]
  const isNew = !row?.code
  const [draft, setDraft] = useState<any>(row || {})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    setDraft(row || {})
    setError('')
    setConfirmDelete(false)
  }, [row])

  async function remove() {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    setSaving(true)
    setError('')
    try {
      await accountingRequest(`/reference/${kind}/${encodeURIComponent(row.code)}`, { method: 'DELETE' })
      onDeleted(row.code)
    } catch (err: any) {
      setError(err.message)
      setConfirmDelete(false)
    } finally {
      setSaving(false)
    }
  }

  async function save(event: any) {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      const code = String(draft.code || '').trim().toUpperCase()
      const body = await accountingRequest(`/reference/${kind}/${encodeURIComponent(code)}`, jsonInit('PUT', { ...draft, active: draft.active !== false }))
      onSaved(body)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={Boolean(row)} onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={save}>
        <DialogTitle sx={{ fontWeight: 700 }}>{isNew ? `New ${spec.singular}` : `${spec.title.slice(0, -1)} ${row.code}`}</DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Stack spacing={2} sx={{ mt: 1 }}>
            {spec.columns.map((column) =>
              column.options ? (
                <TextField
                  key={column.key}
                  select
                  size="small"
                  label={column.label}
                  value={draft[column.key] ?? ''}
                  onChange={(event) => setDraft((current: any) => ({ ...current, [column.key]: event.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {column.options.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  key={column.key}
                  size="small"
                  label={column.label}
                  value={cellValue(draft, column.key)}
                  required={column.key === 'code'}
                  slotProps={column.key === 'code' ? { input: { readOnly: !isNew }, htmlInput: { maxLength: 4, style: { textTransform: 'uppercase' } } } : undefined}
                  onChange={(event) => setDraft((current: any) => ({ ...current, [column.key]: event.target.value }))}
                />
              ),
            )}
            <TextField
              select
              size="small"
              label="Active"
              value={draft.active === false ? 'no' : 'yes'}
              onChange={(event) => setDraft((current: any) => ({ ...current, active: event.target.value === 'yes' }))}
            >
              <MenuItem value="yes">Active</MenuItem>
              <MenuItem value="no">Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!isNew ? (
            <Button type="button" color="error" variant={confirmDelete ? 'contained' : 'text'} disabled={saving} onClick={remove} sx={{ mr: 'auto' }}>
              {confirmDelete ? `Confirm delete ${row.code}` : 'Delete'}
            </Button>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="warning" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default function ReferencePage({ kind }: { kind: ReferenceKind }) {
  const spec = REFERENCE_KINDS[kind]
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState<any>(null)
  const [armed, setArmed] = useState('')
  const [removing, setRemoving] = useState('')

  async function load() {
    setLoading(true)
    setError('')
    try {
      setRows(await accountingRequest(`/reference/${kind}`))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function removeRow(code: string) {
    if (armed !== code) {
      setArmed(code)
      return
    }
    setRemoving(code)
    setError('')
    try {
      await accountingRequest(`/reference/${kind}/${encodeURIComponent(code)}`, { method: 'DELETE' })
      setRows((current) => current.filter((row) => row.code !== code))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setRemoving('')
      setArmed('')
    }
  }

  useEffect(() => {
    setQuery('')
    setEditing(null)
    setArmed('')
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind])

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter((row) => spec.columns.some((column) => String(row[column.key] || '').toLowerCase().includes(needle)))
  }, [rows, query, spec])

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', py: 2 }}>
      <LeoHeader sx={{ mb: 3 }} />
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{spec.title}</Typography>
        <Chip size="small" label={`${filtered.length} of ${rows.length}`} />
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Filter" value={query} onChange={(event) => setQuery(event.target.value)} sx={{ width: 240, bgcolor: 'common.white' }} />
        <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={() => setEditing({ active: true })}>Add</Button>
      </Stack>
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      <TableContainer component={Card} sx={{ mt: 2 }}>
        {loading ? <LinearProgress color="warning" /> : null}
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {spec.columns.map((column) => (
                <TableCell key={column.key} sx={{ bgcolor: 'info.main', color: 'common.white', fontWeight: 700, border: 0, width: column.width }}>
                  {column.label}
                </TableCell>
              ))}
              <TableCell sx={{ bgcolor: 'info.main', color: 'common.white', fontWeight: 700, border: 0, width: 90 }}>Status</TableCell>
              <TableCell sx={{ bgcolor: 'info.main', border: 0, width: 150 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row, index) => (
              <TableRow key={row.code} sx={{ bgcolor: index % 2 ? 'rgba(29, 78, 137, 0.06)' : 'background.paper', opacity: row.active ? 1 : 0.55 }}>
                {spec.columns.map((column) => (
                  <TableCell key={column.key}>
                    {column.key === 'code' ? (
                      <Link component="button" type="button" variant="body2" onClick={() => setEditing(row)} sx={{ fontWeight: 700 }}>
                        {row.code}
                      </Link>
                    ) : (
                      cellValue(row, column.key)
                    )}
                  </TableCell>
                ))}
                <TableCell>
                  <Chip size="small" label={row.active ? 'Active' : 'Inactive'} color={row.active ? 'success' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {armed === row.code ? (
                    <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                      <Button size="small" color="error" variant="contained" disabled={removing === row.code} onClick={() => removeRow(row.code)}>
                        {removing === row.code ? 'Deleting…' : 'Delete'}
                      </Button>
                      <Button size="small" onClick={() => setArmed('')}>Keep</Button>
                    </Stack>
                  ) : (
                    <IconButton size="small" aria-label={`Delete ${row.code}`} onClick={() => removeRow(row.code)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={spec.columns.length + 2}>
                  <Typography variant="body2" color="text.secondary">Nothing matches.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      <EditDialog
        kind={kind}
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={(saved: any) => {
          setRows((current) => {
            const exists = current.some((row) => row.code === saved.code)
            return exists ? current.map((row) => (row.code === saved.code ? saved : row)) : [...current, saved]
          })
          setEditing(null)
        }}
        onDeleted={(code: string) => {
          setRows((current) => current.filter((row) => row.code !== code))
          setEditing(null)
        }}
      />
    </Box>
  )
}
